import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApp } from "@/contexts/AppContext";
import { Shield, KeyRound, Power, Loader2, Copy, Check, Mail, Trash2 } from "lucide-react";
import {
  listAdmins, createAdmin, setAdminPassword, setAdminActive, AdminAccount,
} from "@/api/services/teamService";
import {
  inviteAdmin, removeAdmin, changeMyPassword, type AdminTier,
} from "@/api/services/adminManagementService";
import { getErrorMessage } from "@/utils/errorUtils";

type Note = { type: "success" | "error"; text: string } | null;

export default function TeamAdminAccounts() {
  const { t } = useTranslation();
  const { user, setUser } = useApp();
  const [admins, setAdmins] = useState<AdminAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState<Note>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // set-password modal state
  const [pwFor, setPwFor] = useState<AdminAccount | null>(null);
  const [pwValue, setPwValue] = useState("");
  const [pwSaving, setPwSaving] = useState(false);

  // create-admin form state
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", password: "" });
  const [creating, setCreating] = useState(false);

  // invite-admin form state (Auth0-correct 2-step flow)
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState<{ email: string; adminTier: AdminTier; permissions: string }>({
    email: "", adminTier: "admin", permissions: "",
  });
  const [inviting, setInviting] = useState(false);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [inviteLinkCopied, setInviteLinkCopied] = useState(false);

  // remove-admin confirmation state
  const [removeFor, setRemoveFor] = useState<AdminAccount | null>(null);
  const [removing, setRemoving] = useState(false);

  // change-my-password state
  const [changingPw, setChangingPw] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [changePwForm, setChangePwForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });

  const flash = (type: "success" | "error", text: string) => {
    setNote({ type, text });
    window.setTimeout(() => setNote(null), 4000);
  };

  const load = async () => {
    setLoading(true);
    try { setAdmins(await listAdmins()); }
    catch (e: unknown) { flash("error", getErrorMessage(e) || t("admin.team.errors.load", { defaultValue: "Failed to load admins" })); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const savePassword = async () => {
    if (!pwFor) return;
    setPwSaving(true);
    try {
      await setAdminPassword(pwFor._id, pwValue);
      flash("success", t("admin.team.msg.passwordSet", { defaultValue: "Password updated" }));
      setPwFor(null); setPwValue("");
    } catch (e: unknown) { flash("error", getErrorMessage(e) || t("admin.team.errors.setPassword", { defaultValue: "Failed to set password" })); }
    finally { setPwSaving(false); }
  };

  const toggleActive = async (a: AdminAccount) => {
    setBusyId(a._id);
    try {
      const res = await setAdminActive(a._id, !a.isActive);
      setAdmins((prev) => prev.map((x) => (x._id === a._id ? { ...x, isActive: (res as unknown as Record<string, unknown>).isActive as boolean } : x)));
    } catch (e: unknown) { flash("error", getErrorMessage(e) || t("admin.team.errors.toggle", { defaultValue: "Failed to update account" })); }
    finally { setBusyId(null); }
  };

  const submitCreate = async () => {
    setCreating(true);
    try {
      await createAdmin(form);
      flash("success", t("admin.team.msg.created", { defaultValue: "Admin account created" }));
      setForm({ firstName: "", lastName: "", email: "", password: "" });
      setShowCreate(false);
      load();
    } catch (e: unknown) { flash("error", getErrorMessage(e) || t("admin.team.errors.create", { defaultValue: "Failed to create admin" })); }
    finally { setCreating(false); }
  };

  const openSelfPassword = () => {
    const me = admins.find((a) => a.email === user?.email);
    if (me) { setPwFor(me); setPwValue(""); }
  };

  // Super-admin: invite a new admin (step 1). Shows the invite link in non-prod.
  const submitInvite = async () => {
    setInviting(true);
    setInviteLink(null);
    try {
      const permissions = inviteForm.permissions
        .split(",")
        .map((p) => p.trim())
        .filter(Boolean);
      const res = await inviteAdmin({
        email: inviteForm.email.trim(),
        adminTier: inviteForm.adminTier,
        permissions,
      });
      flash("success", t("admin.team.invite.sent", { defaultValue: "Invitation sent." }));
      setInviteForm({ email: "", adminTier: "admin", permissions: "" });
      if (res.inviteLink) {
        setInviteLink(res.inviteLink); // non-prod convenience
      } else {
        setShowInvite(false);
      }
      load();
    } catch (e: unknown) {
      flash("error", getErrorMessage(e) || t("admin.team.invite.failed", { defaultValue: "Failed to send invitation." }));
    } finally {
      setInviting(false);
    }
  };

  const copyInviteLink = async () => {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      setInviteLinkCopied(true);
      window.setTimeout(() => setInviteLinkCopied(false), 2000);
    } catch { /* clipboard may be unavailable */ }
  };

  // Super-admin: remove an admin (demote-not-destroy). Backend enforces guards.
  const confirmRemove = async () => {
    if (!removeFor) return;
    setRemoving(true);
    try {
      const res = await removeAdmin(removeFor._id);
      flash("success", res.message || t("admin.team.remove.done", { defaultValue: "Admin access removed." }));
      setRemoveFor(null);
      load();
    } catch (e: unknown) {
      // 403 NO_SELF_REMOVAL / CANNOT_REMOVE_SUPER_ADMIN, 404 not an admin.
      flash("error", getErrorMessage(e) || t("admin.team.remove.failed", { defaultValue: "Failed to remove admin." }));
    } finally {
      setRemoving(false);
    }
  };

  const openChangePassword = () => {
    setChangePwForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    setShowChangePw(true);
  };

  // Admin: change own password (requires current + new password).
  const submitChangePassword = async () => {
    const { currentPassword, newPassword, confirmPassword } = changePwForm;
    if (!currentPassword || !newPassword) {
      flash("error", t("admin.team.changePw.required", { defaultValue: "Enter your current and new password." }));
      return;
    }
    if (newPassword.length < 8) {
      flash("error", t("admin.team.changePw.tooShort", { defaultValue: "New password must be at least 8 characters." }));
      return;
    }
    if (newPassword !== confirmPassword) {
      flash("error", t("admin.team.changePw.mismatch", { defaultValue: "New passwords do not match." }));
      return;
    }
    setChangingPw(true);
    try {
      const res = await changeMyPassword({ currentPassword, newPassword });
      flash("success", res.message || t("admin.team.changePw.done", { defaultValue: "Password changed successfully." }));
      setShowChangePw(false);
      setChangePwForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (e: unknown) {
      flash("error", getErrorMessage(e) || t("admin.team.changePw.failed", { defaultValue: "Could not change password." }));
    } finally {
      setChangingPw(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6">
      <div className="flex items-center gap-3 mb-1">
        <Shield className="w-6 h-6 text-blue-600" />
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {t("admin.team.title", { defaultValue: "Team & Admin Accounts" })}
        </h1>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
        {t("admin.team.subtitle", { defaultValue: "Manage admin accounts, reset passwords, and control access." })}
      </p>

      {note && (
        <div className={`mb-4 rounded-lg px-4 py-3 text-sm ${note.type === "success" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
          {note.text}
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-5">
        <button onClick={() => { setShowInvite((s) => !s); setInviteLink(null); }} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2.5 rounded-lg">
          <Mail className="w-4 h-4" /> {t("admin.team.actions.invite", { defaultValue: "Invite admin" })}
        </button>
        <button onClick={openChangePassword} disabled={changingPw} className="inline-flex items-center gap-2 border border-blue-600 text-blue-600 dark:text-blue-400 text-sm font-semibold px-4 py-2.5 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-950 disabled:opacity-50">
          {changingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} {t("admin.team.actions.changeMine", { defaultValue: "Change my password" })}
        </button>
      </div>

      {/* Invite admin (step 1) — super-admin only; backend enforces authorization */}
      {showInvite && (
        <div className="mb-6 rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-900">
          <h3 className="font-semibold mb-3 text-gray-900 dark:text-white">{t("admin.team.invite.title", { defaultValue: "Invite a new admin" })}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input
              type="email"
              value={inviteForm.email}
              onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))}
              placeholder={t("admin.team.invite.email", { defaultValue: "Email" })}
              className="rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2.5 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={inviteForm.adminTier}
              onChange={(e) => setInviteForm((f) => ({ ...f, adminTier: e.target.value as AdminTier }))}
              className="rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2.5 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="admin">{t("admin.team.invite.tierAdmin", { defaultValue: "Admin" })}</option>
              <option value="super-admin">{t("admin.team.invite.tierSuper", { defaultValue: "Super-admin" })}</option>
            </select>
            <input
              type="text"
              value={inviteForm.permissions}
              onChange={(e) => setInviteForm((f) => ({ ...f, permissions: e.target.value }))}
              placeholder={t("admin.team.invite.permissions", { defaultValue: "Permissions (comma-separated)" })}
              className="rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2.5 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="mt-3 flex items-center gap-3">
            <button onClick={submitInvite} disabled={inviting || !inviteForm.email.trim()} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2.5 rounded-lg disabled:opacity-50">
              {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} {t("admin.team.invite.send", { defaultValue: "Send invitation" })}
            </button>
            <button onClick={() => { setShowInvite(false); setInviteLink(null); }} className="text-sm text-gray-500 hover:underline">
              {t("admin.team.invite.cancel", { defaultValue: "Cancel" })}
            </button>
          </div>
          {inviteLink && (
            <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-900/50 p-3">
              <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-1">{t("admin.team.invite.linkTitle", { defaultValue: "Invite link (non-production)" })}</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 truncate text-xs text-amber-900 dark:text-amber-200">{inviteLink}</code>
                <button onClick={copyInviteLink} className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800 dark:text-amber-300 hover:underline">
                  {inviteLinkCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {inviteLinkCopied ? t("admin.team.invite.copied", { defaultValue: "Copied" }) : t("admin.team.invite.copy", { defaultValue: "Copy" })}
                </button>
              </div>
            </div>
          )}
        </div>
      )}


      {showCreate && (
        <div className="mb-6 rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-900">
          <h3 className="font-semibold mb-3 text-gray-900 dark:text-white">{t("admin.team.actions.create", { defaultValue: "Create admin" })}</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <input className="border rounded-lg px-3 py-2 text-sm dark:bg-gray-800 dark:border-gray-700" placeholder={t("admin.team.fields.firstName", { defaultValue: "First name" })} value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
            <input className="border rounded-lg px-3 py-2 text-sm dark:bg-gray-800 dark:border-gray-700" placeholder={t("admin.team.fields.lastName", { defaultValue: "Last name" })} value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
            <input className="border rounded-lg px-3 py-2 text-sm dark:bg-gray-800 dark:border-gray-700" placeholder={t("admin.team.fields.email", { defaultValue: "Email" })} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input type="password" className="border rounded-lg px-3 py-2 text-sm dark:bg-gray-800 dark:border-gray-700" placeholder={t("admin.team.fields.password", { defaultValue: "Temp password" })} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </div>
          <p className="text-xs text-gray-500 mt-2">{t("admin.team.hints.password", { defaultValue: "Min 8 chars, with uppercase, lowercase, number and special character." })}</p>
          <button onClick={submitCreate} disabled={creating} className="mt-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-semibold px-4 py-2 rounded-lg inline-flex items-center gap-2">
            {creating && <Loader2 className="w-4 h-4 animate-spin" />} {t("admin.team.actions.save", { defaultValue: "Create" })}
          </button>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800 text-left text-gray-600 dark:text-gray-300">
            <tr>
              <th className="px-4 py-3 font-semibold">{t("admin.team.cols.name", { defaultValue: "Name" })}</th>
              <th className="px-4 py-3 font-semibold">{t("admin.team.cols.email", { defaultValue: "Email" })}</th>
              <th className="px-4 py-3 font-semibold">{t("admin.team.cols.role", { defaultValue: "Role" })}</th>
              <th className="px-4 py-3 font-semibold">{t("admin.team.cols.status", { defaultValue: "Status" })}</th>
              <th className="px-4 py-3 font-semibold text-right">{t("admin.team.cols.actions", { defaultValue: "Actions" })}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
            ) : admins.map((a) => (
              <tr key={a._id} className="text-gray-800 dark:text-gray-200">
                <td className="px-4 py-3">{a.firstName} {a.lastName}</td>
                <td className="px-4 py-3">{a.email}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-semibold px-2 py-1 rounded ${a.role === "super-admin" ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-700"}`}>{a.role}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-semibold px-2 py-1 rounded ${a.isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                    {a.isActive ? t("admin.team.status.active", { defaultValue: "Active" }) : t("admin.team.status.disabled", { defaultValue: "Disabled" })}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => { setPwFor(a); setPwValue(""); }} className="inline-flex items-center gap-1 text-xs font-semibold border border-gray-300 dark:border-gray-600 px-3 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800">
                      <KeyRound className="w-3.5 h-3.5" /> {t("admin.team.actions.setPassword", { defaultValue: "Set password" })}
                    </button>
                    {a.role !== "super-admin" && (
                      <button onClick={() => toggleActive(a)} disabled={busyId === a._id} className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg border disabled:opacity-50 ${a.isActive ? "border-red-300 text-red-600 hover:bg-red-50" : "border-green-300 text-green-600 hover:bg-green-50"}`}>
                        {busyId === a._id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Power className="w-3.5 h-3.5" />}
                        {a.isActive ? t("admin.team.actions.disable", { defaultValue: "Disable" }) : t("admin.team.actions.enable", { defaultValue: "Enable" })}
                      </button>
                    )}
                    {/* Remove admin (demote-not-destroy). Hidden for super-admins and
                        for yourself; the backend also enforces these guards. */}
                    {a.role !== "super-admin" && a.email !== user?.email && (
                      <button onClick={() => setRemoveFor(a)} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 dark:border-red-900/50 dark:text-red-400 dark:hover:bg-red-900/20">
                        <Trash2 className="w-3.5 h-3.5" /> {t("admin.team.actions.remove", { defaultValue: "Remove access" })}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pwFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) setPwFor(null); }}>
          <div className="bg-white dark:bg-gray-900 rounded-xl p-5 w-full max-w-sm">
            <h3 className="font-bold text-gray-900 dark:text-white mb-1">{t("admin.team.setPw.title", { defaultValue: "Set new password" })}</h3>
            <p className="text-sm text-gray-500 mb-3">{pwFor.email}</p>
            <input type="password" autoFocus className="w-full border rounded-lg px-3 py-2 text-sm dark:bg-gray-800 dark:border-gray-700" placeholder={t("admin.team.fields.newPassword", { defaultValue: "New password" })} value={pwValue} onChange={(e) => setPwValue(e.target.value)} />
            <p className="text-xs text-gray-500 mt-2">{t("admin.team.hints.password", { defaultValue: "Min 8 chars, with uppercase, lowercase, number and special character." })}</p>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setPwFor(null)} className="text-sm px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600">{t("admin.team.actions.cancel", { defaultValue: "Cancel" })}</button>
              <button onClick={savePassword} disabled={pwSaving} className="text-sm px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold inline-flex items-center gap-2">
                {pwSaving && <Loader2 className="w-4 h-4 animate-spin" />} {t("admin.team.actions.save2", { defaultValue: "Save" })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove-admin confirmation */}
      {removeFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) setRemoveFor(null); }}>
          <div className="bg-white dark:bg-gray-900 rounded-xl p-5 w-full max-w-sm">
            <h3 className="font-bold text-gray-900 dark:text-white mb-1">{t("admin.team.remove.title", { defaultValue: "Remove admin access" })}</h3>
            <p className="text-sm text-gray-500 mb-3">
              {t("admin.team.remove.confirm", {
                defaultValue: "This removes admin access for {{name}} ({{email}}). Their account is suspended and demoted — not deleted — and the audit history is kept.",
                name: `${removeFor.firstName} ${removeFor.lastName}`.trim(),
                email: removeFor.email,
              })}
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setRemoveFor(null)} className="text-sm px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600">{t("admin.team.actions.cancel", { defaultValue: "Cancel" })}</button>
              <button onClick={confirmRemove} disabled={removing} className="text-sm px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-semibold inline-flex items-center gap-2">
                {removing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} {t("admin.team.remove.cta", { defaultValue: "Remove access" })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change my password */}
      {showChangePw && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowChangePw(false); }}>
          <div className="bg-white dark:bg-gray-900 rounded-xl p-5 w-full max-w-sm">
            <h3 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <KeyRound className="w-4 h-4" /> {t("admin.team.changePw.title", { defaultValue: "Change my password" })}
            </h3>
            <div className="space-y-3">
              <input
                type="password"
                autoComplete="current-password"
                value={changePwForm.currentPassword}
                onChange={(e) => setChangePwForm((f) => ({ ...f, currentPassword: e.target.value }))}
                placeholder={t("admin.team.changePw.current", { defaultValue: "Current password" })}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2.5 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="password"
                autoComplete="new-password"
                value={changePwForm.newPassword}
                onChange={(e) => setChangePwForm((f) => ({ ...f, newPassword: e.target.value }))}
                placeholder={t("admin.team.changePw.new", { defaultValue: "New password" })}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2.5 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="password"
                autoComplete="new-password"
                value={changePwForm.confirmPassword}
                onChange={(e) => setChangePwForm((f) => ({ ...f, confirmPassword: e.target.value }))}
                placeholder={t("admin.team.changePw.confirm", { defaultValue: "Confirm new password" })}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2.5 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-400">{t("admin.team.changePw.hint", { defaultValue: "Min 8 characters, with uppercase, lowercase, number and special character." })}</p>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowChangePw(false)} className="text-sm px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600">{t("admin.team.actions.cancel", { defaultValue: "Cancel" })}</button>
              <button onClick={submitChangePassword} disabled={changingPw} className="text-sm px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold inline-flex items-center gap-2">
                {changingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} {t("admin.team.changePw.cta", { defaultValue: "Change password" })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
